import { useMemo } from "react";
import { FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useSearchCompanySectors, CompanySector } from "src/api/client-api/companySectors/useSupplierSectors";

export interface CompanySectorOption {
    "@id": string;
    id: number;
    name: string;
}

interface FormCompanySelectorProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const FormCompanySectorSelector = ({ value, onChange, disabled = false }: FormCompanySelectorProps) => {
    const { t } = useTranslation();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    // Use search hook when there's a search term, otherwise load initial sectors
    const searchEnabled = delayedSearch.length >= 2;

    const { companySectors, isLoading } = useSearchCompanySectors(searchEnabled ? delayedSearch : undefined, {
        enabled: true,
    });

    // Filter sectors that have @id and convert to our option type
    const sectorOptions: CompanySectorOption[] = useMemo(() => {
        return companySectors
            .filter((sector): sector is CompanySector & { "@id": string } => Boolean(sector["@id"]))
            .map((sector) => ({
                "@id": sector["@id"],
                id: sector.id,
                name: sector.name,
            }));
    }, [companySectors]);

    const selectedSector = sectorOptions.find((sector) => sector["@id"] === value);

    const isSearchFound = (searchValue: string) => {
        // For server-side search, always return true as results are already filtered
        return true;
    };

    return (
        <FormControl fullWidth>
            <FoodsSelectServerPagination
                selectedOption={selectedSector || null}
                options={isLoading ? [] : sectorOptions}
                onChange={(sector) => onChange(sector?.["@id"] || "")}
                getId={(sector: CompanySectorOption) => sector["@id"]}
                getName={(sector: CompanySectorOption) => sector.name}
                textOptions={{
                    placeholder: t("companySector.select.placeholder", "Select an industry sector"),
                    searchPlaceholder: t("companySector.select.searchPlaceholder", "Search industry sectors..."),
                    emptyOptionsText: t("companySector.select.noOptions", "No industry sectors available"),
                    defaultValueText: t("companySector.select.defaultValue", "Default value"),
                    noResult:
                        isLoading ?
                            t("companySector.select.loading", "Loading...")
                        :   t("companySector.select.noResultFound", "No industry sectors found"),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={isSearchFound}
                disabled={disabled}
            />
        </FormControl>
    );
};
